import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  User as FirebaseUser,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from 'firebase/auth';
import { auth } from '../firebase/config';
import { User } from '../types';
import { doc, setDoc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName?: string) => Promise<void>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Firestoreからユーザー情報を取得（role含む）
  const fetchUserFromFirestore = useCallback(async (firebaseUser: FirebaseUser): Promise<User> => {
    try {
      const userRef = doc(db, 'users', firebaseUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const data = userSnap.data();
        return {
          id: firebaseUser.uid,
          email: data.email || firebaseUser.email!,
          displayName: data.displayName || firebaseUser.displayName || undefined,
          photoURL: data.photoURL || firebaseUser.photoURL || undefined,
          role: data.role || 'viewer',
          createdAt: data.createdAt?.toDate() || new Date(firebaseUser.metadata.creationTime!),
          updatedAt: data.updatedAt?.toDate() || new Date(firebaseUser.metadata.lastSignInTime!),
        };
      }
    } catch (error) {
      console.error('Firestoreからのユーザー情報取得に失敗しました:', error);
    }
    
    // Firestoreに存在しない場合は、Firebase Authの情報を使用（roleはviewerをデフォルト）
    return {
      id: firebaseUser.uid,
      email: firebaseUser.email!,
      displayName: firebaseUser.displayName || undefined,
      photoURL: firebaseUser.photoURL || undefined,
      role: 'viewer',
      createdAt: new Date(firebaseUser.metadata.creationTime!),
      updatedAt: new Date(firebaseUser.metadata.lastSignInTime!),
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err: unknown) {
      const error = err as { code?: string; message?: string };
      // 代表的なエラーを絞って明示
      const code = error.code || 'unknown/error';
      switch (code) {
        case 'auth/invalid-api-key':
        case 'invalid-api-key':
          console.error('Firebase APIキーが無効です。FirebaseコンソールのWebアプリ設定のapiKeyを見直してください。');
          break;
        case 'auth/invalid-email':
          console.error('メールアドレス形式が不正です。');
          break;
        case 'auth/user-not-found':
          console.error('ユーザーが存在しません。Authentication > ユーザー に作成してください。');
          break;
        case 'auth/wrong-password':
          console.error('パスワードが正しくありません。');
          break;
        case 'auth/operation-not-allowed':
          console.error('メール/パスワード認証が無効です。Authentication > ログイン方法 で有効化してください。');
          break;
        default:
          console.error('認証エラー:', code, error.message);
      }
      throw err;
    }
  };

  const register = async (email: string, password: string, displayName?: string) => {
    const { user } = await createUserWithEmailAndPassword(auth, email, password);
    if (displayName) {
      await updateProfile(user, { displayName });
    }
    
    // Firestoreにユーザー情報を保存
    try {
      const userData = {
        email: user.email!,
        displayName: displayName || null,
        photoURL: user.photoURL || null,
        role: 'viewer',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
      await setDoc(doc(db, 'users', user.uid), userData);
    } catch (error) {
      console.error('Firestoreへのユーザー情報保存に失敗しました:', error);
      // エラーが発生しても登録は成功とする（Firebase Authenticationは成功しているため）
    }
  };

  const logout = async () => {
    await signOut(auth);
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    const user = auth.currentUser;
    if (!user || !user.email) {
      throw new Error('ログインしていません。');
    }
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setLoading(true);
        // Firestoreからユーザー情報を取得（role含む）
        try {
          // まずFirestoreから取得
          let user = await fetchUserFromFirestore(firebaseUser);
          console.log('AuthContext - Firestoreから取得したユーザー情報:', user);
          
          // Firestoreにユーザーが存在しない場合は同期
          const { UserService } = await import('../services/userService');
          await UserService.syncCurrentUser();
          
          // 同期後に再度取得して最新の情報を反映
          user = await fetchUserFromFirestore(firebaseUser);
          console.log('AuthContext - 同期後のユーザー情報:', user);
          setCurrentUser(user);
        } catch (error) {
          console.error('ユーザー情報取得エラー:', error);
          // エラーが発生してもFirebase Authの情報を使用
          const fallbackUser = await fetchUserFromFirestore(firebaseUser);
          setCurrentUser(fallbackUser);
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [fetchUserFromFirestore]);

  const value: AuthContextType = {
    currentUser,
    loading,
    login,
    register,
    logout,
    changePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
